import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const KeyboardShortcuts = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-focus first editable field across all master forms on route change
  useEffect(() => {
    const timer = setTimeout(() => {
      const container = document.querySelector('form') || document.querySelector('.modal-content') || document.querySelector('.content-body');
      if (container) {
        const isVisible = (el) => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
        };
        const firstInput = Array.from(container.querySelectorAll(
          'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
        )).filter(isVisible)[0];

        if (firstInput && (!document.activeElement || document.activeElement === document.body)) {
          firstInput.focus();
          if (typeof firstInput.select === 'function' && firstInput.type !== 'checkbox' && firstInput.type !== 'radio') {
            try { firstInput.select(); } catch {}
          }
        }
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const setupTableRows = () => {
      const rows = document.querySelectorAll('tbody tr');
      rows.forEach(row => {
        if (!row.hasAttribute('tabindex')) {
          row.setAttribute('tabindex', '0');
          row.style.cursor = 'pointer';
        }
      });
    };

    // Run immediately
    setupTableRows();

    // Create a MutationObserver to observe DOM changes and update newly added table rows
    const observer = new MutationObserver(setupTableRows);
    observer.observe(document.body, { childList: true, subtree: true });

    const handleToggleEvent = () => {
      setIsOpen(prev => !prev);
    };
    window.addEventListener('toggle-keyboard-shortcuts', handleToggleEvent);

    return () => {
      observer.disconnect();
      window.removeEventListener('toggle-keyboard-shortcuts', handleToggleEvent);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.defaultPrevented) return;
      // Checkbox +/- handler: '+' to tick (check), '-' to untick (uncheck)
      const active = document.activeElement;
      let checkboxEl = null;

      if (active) {
        if (active.tagName === 'INPUT' && active.type === 'checkbox') {
          checkboxEl = active;
        } else if (active.getAttribute && active.getAttribute('role') === 'checkbox') {
          checkboxEl = active;
        } else if (active.tagName === 'LABEL' && active.htmlFor) {
          const linked = document.getElementById(active.htmlFor);
          if (linked && linked.type === 'checkbox') {
            checkboxEl = linked;
          }
        } else if (active.querySelector && active.querySelector('input[type="checkbox"]')) {
          checkboxEl = active.querySelector('input[type="checkbox"]');
        }
      }

      if (checkboxEl) {
        const isPlus = e.key === '+' || e.key === 'Add' || e.code === 'NumpadAdd' || (e.key === '=' && e.shiftKey);
        const isMinus = e.key === '-' || e.key === 'Subtract' || e.code === 'NumpadSubtract' || (e.key === '-' && !e.ctrlKey && !e.altKey);

        if (isPlus) {
          e.preventDefault();
          e.stopPropagation();
          if (checkboxEl.tagName === 'INPUT' && checkboxEl.type === 'checkbox') {
            if (!checkboxEl.checked) {
              checkboxEl.click();
            }
          } else {
            if (checkboxEl.getAttribute('aria-checked') !== 'true') {
              checkboxEl.click();
            }
          }
          return;
        }

        if (isMinus) {
          e.preventDefault();
          e.stopPropagation();
          if (checkboxEl.tagName === 'INPUT' && checkboxEl.type === 'checkbox') {
            if (checkboxEl.checked) {
              checkboxEl.click();
            }
          } else {
            if (checkboxEl.getAttribute('aria-checked') === 'true') {
              checkboxEl.click();
            }
          }
          return;
        }
      }

      // Escape key
      if (e.key === 'Escape') {
        if (isOpen) {
          setIsOpen(false);
          e.preventDefault();
          return;
        }
        // Cancel forms
        const cancelBtn = Array.from(document.querySelectorAll('button, a')).find(el => 
          el.textContent.includes('Cancel') || el.className.includes('btn-danger') || el.className.includes('light')
        );
        if (cancelBtn) {
          cancelBtn.click();
          e.preventDefault();
          return;
        }
      }

      // Toggle Help Modal with Alt+K
      if (e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen(prev => !prev);
        return;
      }

      // Direct routing shortcuts with Alt + Number
      if (e.altKey) {
        const routes = {
          '1': '/FirmMaster',
          '2': '/Payroll',
          '3': '/DepartmentMaster',
          '4': '/DesignationMaster',
          '5': '/EmployeeMaster',
          '6': '/ConcernTypeMaster',
          '7': '/PremisesTypeMaster',
          '8': '/WorkerCategory',
          '9': '/LeaveMaster',
          '0': '/AllowanceMaster',
        };
        if (routes[e.key]) {
          e.preventDefault();
          navigate(routes[e.key]);
          setIsOpen(false);
          return;
        }

        // Alt + H or Alt + D for Dashboard/Home
        if (e.key === 'h' || e.key === 'H' || e.key === 'd' || e.key === 'D') {
          e.preventDefault();
          navigate('/');
          setIsOpen(false);
          return;
        }

        // Alt + N for Add New Button
        if (e.key === 'n' || e.key === 'N') {
          const addNewBtn = Array.from(document.querySelectorAll('button, a')).find(el => 
            el.textContent.toLowerCase().includes('add new') || 
            el.textContent.trim() === 'Add' || 
            (el.tagName === 'BUTTON' && el.querySelector('.fa-plus')) ||
            (el.tagName === 'A' && el.querySelector('.fa-plus'))
          );
          if (addNewBtn) {
            e.preventDefault();
            addNewBtn.click();
            return;
          }
        }

        // Alt + F or Alt + S for Search/Filter Focus
        if (e.key === 'f' || e.key === 'F') {
          const searchInput = document.querySelector('.input-search, input[type="search"], input[placeholder*="Search"], input[placeholder*="Filter"]');
          if (searchInput) {
            e.preventDefault();
            searchInput.focus();
            return;
          }
        }

        // Alt + S to Submit form
        if (e.key === 's' || e.key === 'S') {
          const submitBtn = document.querySelector('button[type="submit"]');
          if (submitBtn) {
            e.preventDefault();
            submitBtn.click();
            return;
          }
        }

        // Alt + C to Cancel
        if (e.key === 'c' || e.key === 'C') {
          const cancelBtn = Array.from(document.querySelectorAll('button, a')).find(el => 
            el.textContent.includes('Cancel') || el.className.includes('btn-danger') || el.className.includes('light')
          );
          if (cancelBtn) {
            e.preventDefault();
            cancelBtn.click();
            return;
          }
        }
      }

      // Ctrl + S to Save/Submit form
      if (e.ctrlKey && (e.key === 's' || e.key === 'S')) {
        const submitBtn = document.querySelector('button[type="submit"]');
        if (submitBtn) {
          e.preventDefault();
          submitBtn.click();
          return;
        }
      }

      // Enhanced Enter Key Navigation across all forms (Section 9 Requirement)
      if (e.key === 'Enter') {
        if (!active) return;

        // Skip if active is a submit/button/link/textarea with shift or active is already a button being clicked intentionally
        const isButtonOrSubmit = active.tagName === 'BUTTON' || (active.tagName === 'INPUT' && (active.type === 'submit' || active.type === 'button'));
        if (isButtonOrSubmit) {
          // If Enter was pressed on a Next button, auto-focus first input of the newly active tab
          const isNextButton = active.id === 'btnNextTab' ||
            active.id?.toLowerCase().includes('next') ||
            active.textContent.trim().toLowerCase().startsWith('next');
          if (isNextButton) {
            setTimeout(() => {
              const activeContainer = document.querySelector('.emp-tab-container') || document.querySelector('.tab-pane-content') || document.querySelector('form') || document.querySelector('.content-body');
              if (activeContainer) {
                const isVisible = (el) => {
                  const style = window.getComputedStyle(el);
                  return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
                };
                const nextInputs = Array.from(activeContainer.querySelectorAll(
                  'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
                )).filter(isVisible);
                if (nextInputs.length > 0) {
                  nextInputs[0].focus();
                  if (typeof nextInputs[0].select === 'function' && nextInputs[0].type !== 'checkbox' && nextInputs[0].type !== 'radio') {
                    try { nextInputs[0].select(); } catch {}
                  }
                }
              }
            }, 150);
          }
          // Allow normal button activation on Enter
          return;
        }

        // If in textarea and Shift key is pressed, allow new line
        if (active.tagName === 'TEXTAREA' && e.shiftKey) {
          return;
        }

        // Check if user is currently navigating inside an open React-Select / custom dropdown / autocomplete menu
        const isOpenSelectMenu = document.querySelector('.select__menu, [class*="-menu"]') !== null &&
          (active.getAttribute('aria-expanded') === 'true' || active.closest('[class*="-control"]'));
        if (isOpenSelectMenu) {
          // Allow React Select to process Enter to select the item
          return;
        }

        // Check validation status: if current field is marked invalid, keep focus on invalid field
        const isInvalid = active.classList.contains('is-invalid') ||
          active.getAttribute('aria-invalid') === 'true' ||
          (active.validity && !active.validity.valid);
        if (isInvalid) {
          return;
        }

        // Identify form or component container (prioritize modal container, then form, so modals/tabs inputs are navigated in their own scope)
        const container = active.closest('[data-modal-container], .modal-content') ||
          active.closest('form') ||
          active.closest('main, .content-body, .card-body, .card') ||
          document.body;
        
        if (container) {
          // Query all candidate focusable form input elements (exclude action buttons from input flow)
          const allInputs = Array.from(container.querySelectorAll(
            'input:not([type="hidden"]):not([disabled]):not([readonly]), ' +
            'select:not([disabled]), ' +
            'textarea:not([disabled]):not([readonly])'
          ));

          // Filter visible items
          const focusables = allInputs.filter(el => {
            const style = window.getComputedStyle(el);
            const isVisible = style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              el.offsetWidth > 0 &&
              el.offsetHeight > 0;
            return isVisible;
          });

          const index = focusables.indexOf(active);

          if (index > -1) {
            e.preventDefault();
            e.stopPropagation();

            if (index < focusables.length - 1) {
              const nextEl = focusables[index + 1];
              nextEl.focus();
              if (typeof nextEl.select === 'function' && nextEl.tagName === 'INPUT' && nextEl.type !== 'checkbox' && nextEl.type !== 'radio') {
                try { nextEl.select(); } catch {}
              }
            } else {
              // On the last field: shift focus to Next button if available, or primary Save/submit button
              const isVisible = (el) => {
                if (!el) return false;
                const style = window.getComputedStyle(el);
                return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
              };
              const nextBtn = document.getElementById('btnNextTab') ||
                Array.from(container.querySelectorAll('button')).find(b => b.id?.toLowerCase().includes('next') || b.textContent.trim().toLowerCase().startsWith('next'));
              if (nextBtn && isVisible(nextBtn)) {
                nextBtn.focus();
              } else {
                const submitBtn = document.getElementById('btnSubmitForm') ||
                  container.querySelector('[data-modal-save]') ||
                  focusables.find(el => el.tagName === 'BUTTON' || el.type === 'submit') ||
                  container.querySelector('button[type="submit"], button.btn-save, button.btn-primary, button.btn-success');
                if (submitBtn) {
                  submitBtn.focus();
                }
              }
            }
          }
        }     
      }

      // Table rows navigation
      if (active && active.tagName === 'TR') {
        const parentTbody = active.closest('tbody');
        if (parentTbody) {
          const rows = Array.from(parentTbody.querySelectorAll('tr'));
          const index = rows.indexOf(active);

          if (e.key === 'ArrowDown') {
            if (index < rows.length - 1) {
              e.preventDefault();
              rows[index + 1].focus();
            }
          } else if (e.key === 'ArrowUp') {
            if (index > 0) {
              e.preventDefault();
              rows[index - 1].focus();
            }
          } else if (e.key === 'Enter') {
            const editBtn = active.querySelector('button.btn-warning, a.btn-warning, button[title*="Edit"], a[title*="Edit"], .fa-edit');
            if (editBtn) {
              e.preventDefault();
              if (editBtn.tagName === 'I') {
                editBtn.closest('button, a')?.click();
              } else {
                editBtn.click();
              }
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, navigate]);

  if (!isOpen) return null;

  // Inline premium glassmorphism styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(8px)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Poppins', sans-serif"
  };

  const modalContentStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
    borderRadius: '16px',
    maxWidth: '650px',
    width: '100%',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const headerStyle = {
    padding: '18px 24px',
    borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'linear-gradient(135deg, rgba(76, 110, 245, 0.05), rgba(121, 80, 242, 0.05))'
  };

  const titleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#344767',
    letterSpacing: '-0.3px'
  };
  
  const closeBtnStyle = {
    border: 'none',
    background: 'transparent',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#8da2bb',
    lineHeight: 1,
    padding: 0,
    outline: 'none',
    transition: 'color 0.2s',
    display: 'flex',
    alignItems: 'center'
  };

  const bodyStyle = {
    padding: '24px',
    overflowY: 'auto'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '24px'
  };

  const tableHeaderRowStyle = {
    borderBottom: '2px solid rgba(226, 232, 240, 0.8)'
  };

  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#67748E',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const tdStyle = {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#344767',
    borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
  };

  const tdShortcutStyle = {
    padding: '10px 12px',
    width: '180px',
    borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
  };

  const subTitleStyle = {
    fontSize: '15px',
    fontWeight: '600',
    color: '#344767',
    marginBottom: '14px',
    marginTop: '10px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px 16px',
    background: 'rgba(76, 110, 245, 0.04)',
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid rgba(76, 110, 245, 0.08)'
  };

  const gridItemStyle = {
    fontSize: '13px',
    color: '#344767',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const kbdStyle = {
    background: '#f1f5f9',
    border: '1px solid #cbd5e1',
    padding: '2px 6px',
    borderRadius: '4px',
    boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
    color: '#1e293b', // Explicit dark slate color to override bootstrap default white color
    fontFamily: 'monospace',
    fontWeight: 'bold',
    fontSize: '11px',
    display: 'inline-block'
  };

  const gridKbdStyle = {
    ...kbdStyle,
    padding: '2px 4px'
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            ⌨️ Keyboard Shortcuts Help
          </h3>
          <button style={closeBtnStyle} onClick={() => setIsOpen(false)}>×</button>
        </div>
        <div style={bodyStyle}>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Shortcut</th>
                <th style={thStyle}>Action Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Alt + K</kbd></td>
                <td style={tdStyle}>Toggle Keyboard Shortcuts Cheatsheet (This Modal)</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Alt + H</kbd> / <kbd style={kbdStyle}>Alt + D</kbd></td>
                <td style={tdStyle}>Navigate to Dashboard / Home</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Alt + N</kbd></td>
                <td style={tdStyle}>Trigger "Add New" button on list pages</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Alt + F</kbd></td>
                <td style={tdStyle}>Focus Search/Filter Input on List Tables</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Ctrl + S</kbd> / <kbd style={kbdStyle}>Alt + S</kbd></td>
                <td style={tdStyle}>Submit/Save active Form</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Alt + C</kbd> / <kbd style={kbdStyle}>Escape</kbd></td>
                <td style={tdStyle}>Cancel edit / Close modals</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Enter</kbd> (in inputs)</td>
                <td style={tdStyle}>Move focus to next field (Enter-as-Tab)</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>↑</kbd> / <kbd style={kbdStyle}>↓</kbd></td>
                <td style={tdStyle}>Navigate through table rows (focus a row first)</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>+</kbd> (on checkbox)</td>
                <td style={tdStyle}>Tick / Check the focused checkbox</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>-</kbd> (on checkbox)</td>
                <td style={tdStyle}>Untick / Uncheck the focused checkbox</td>
              </tr>
              <tr>
                <td style={tdShortcutStyle}><kbd style={kbdStyle}>Enter</kbd> (on table row)</td>
                <td style={tdStyle}>Trigger Edit action for selected row</td>
              </tr>
            </tbody>
          </table>

          <h4 style={subTitleStyle}>Direct Page Navigation (Alt + Number)</h4>
          <div style={gridStyle}>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 1</kbd> Firm Master</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 2</kbd> Payroll Form</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 3</kbd> Department Master</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 4</kbd> Designation Master</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 5</kbd> Employee Master</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 6</kbd> Concern Type</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 7</kbd> Premises Type</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 8</kbd> Worker Category</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 9</kbd> Leave Master</div>
            <div style={gridItemStyle}><kbd style={gridKbdStyle}>Alt + 0</kbd> Allowance Master</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
